import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { RootState } from "redux/store";
import { bindActionCreators, Dispatch } from "redux";
import { connect } from "react-redux";
import { NewRoundRobinRoute, RoundRobinRoute } from "model/round-robin-route";
import * as Router from "react-router-dom";
import { styles } from "./styles";
import { updateRoundRobinRoutes } from "redux/slice/round-robin-routes";
import { FeatherIcon } from "component/shared/feather-icon";
import * as Icons from "react-feather";
import { getUserUnsafe } from "redux/selector";
import { fetchRoundRobinRoutes } from "redux/slice/round-robin-routes"
import { Switch } from "component/shared/switch";
import { saveUser } from "redux/slice/authentication";
import { UserPartial } from "model/user";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Card } from "./card";
import { RefreshCard } from "./card-refresh";
import { deepCompare } from "shared/deep-compare";
import { AddDialog } from "./add-dialog";
import { RefreshAddDialog } from "./add-dialog-refresh";
import { Agent } from "model/agent";
import { ConfirmationModal } from "../confirmation-modal";
import * as env from "shared/env";
import { baseColors } from "theme";

interface OwnProps {
	roundRobinRoutes: RoundRobinRoute[];
	checked?: boolean;
	onToggle?: (checked: boolean) => void;
}

interface State {
	routes: RoundRobinRoute[];
	enabled: boolean;
	dialogOpen: boolean;
	removeIndex: number | null;
	removeEntirely: boolean;
}


const mapStateToProps = (state: RootState) => {
	const user = getUserUnsafe(state);
	return {
		user,
	};
};

interface Props	extends OwnProps,
	ReturnType<typeof mapDispatchToProps>,
	ReturnType<typeof mapStateToProps>,
	WithStyles<typeof styles>,
	Router.RouteComponentProps
{}

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	updateRoundRobinRoutes,
	fetchRoundRobinRoutes,
	saveUser,
}, dispatch);

class Component extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			routes: this.props.roundRobinRoutes,
			enabled: props.user && props.user.roundRobinRouting ? true : false,
			dialogOpen: false,
			removeIndex: null,
			removeEntirely: false,
		};
	}

	public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
		const { roundRobinRoutes } = this.props;
		if (!deepCompare(prevProps.roundRobinRoutes, roundRobinRoutes) ) {
			this.setState({routes: roundRobinRoutes});
		}
	}

	private onDragEnd = (result: any) => {
		const { destination, source, draggableId } = result;
		if (!destination) {
			return;
		}
		if (destination.droppableId === source.droppableId && destination.index === source.index) {
			return;
		}
		const items = Array.from(this.state.routes);
		const [reordered] = items.splice(source.index, 1);
		items.splice(destination.index, 0, reordered);
		this.setState({
			routes: items,
		}, () => this.saveRoutes());
	};

	private saveRoutes = async () => {
		this.props.updateRoundRobinRoutes({ roundRobinRoutes: this.state.routes });
	}

	// Move an agent up/down in the rotation via the position steppers.
	private move = (from: number, to: number) => {
		if (to < 0 || to >= this.state.routes.length) {
			return;
		}
		const items = Array.from(this.state.routes);
		const [moved] = items.splice(from, 1);
		items.splice(to, 0, moved);
		this.setState({ routes: items }, () => this.saveRoutes());
	}

	private saveUser() {
		const { saveUser } = this.props;
		const { enabled } = this.state;
		const user: UserPartial = {
			roundRobinRouting: enabled,
		}
		saveUser({user});
	}

	// Refreshed rotation list (flag on): single bordered container, position steppers, "Next up"
	// tag, and removal via the confirmation modal.
	private renderRefreshRotationList() {
		const { routes } = this.state;
		const { user } = this.props;
		// The agent next in line to receive a lead, per the saved rotation pointer.
		const nextIndex = user?.roundRobinPosition ? user.roundRobinPosition - 1 : 0;
		return (
			<DragDropContext onDragEnd={this.onDragEnd}>
				<Droppable droppableId="droppable">
					{(provided) => (
						<div
							{...provided.droppableProps}
							ref={provided.innerRef}
							style={{ border: `1px solid ${baseColors.gray100}`, borderRadius: 8, overflow: "hidden", maxWidth: 960 }}
						>
							{routes.map((route, index) => (
								<Draggable key={route.id} draggableId={route.id.toString()} index={index}>
									{(provided) => (
										<div
											ref={provided.innerRef}
											{...provided.draggableProps}
											style={{
												...provided.draggableProps.style,
												background: baseColors.white,
												borderBottom: index < routes.length - 1 ? `1px solid ${baseColors.gray100}` : undefined,
											}}
										>
											<RefreshCard
												position={index + 1}
												isNext={index === nextIndex}
												roundRobinRoute={route}
												dragHandleProps={provided.dragHandleProps}
												disableUp={index === 0}
												disableDown={index === routes.length - 1}
												onMoveUp={() => this.move(index, index - 1)}
												onMoveDown={() => this.move(index, index + 1)}
												onDelete={() => this.setState({ removeIndex: index, removeEntirely: false })}
											/>
										</div>
									)}
								</Draggable>
							))}
							{provided.placeholder}
						</div>
					)}
				</Droppable>
			</DragDropContext>
		);
	}

	// Legacy rotation list (flag off): original AppCard rows with direct (window.confirm) removal.
	private renderRotationList() {
		const { routes } = this.state;
		return (
			<DragDropContext onDragEnd={this.onDragEnd}>
				<Droppable droppableId="droppable">
					{(provided) => (
						<Mui.Grid container direction="column" spacing={2} {...provided.droppableProps} ref={provided.innerRef}>
							{routes.map((route, index) => (
								<Draggable key={route.id} draggableId={route.id.toString()} index={index}>
									{(provided) => (
										<Mui.Grid item
											ref={provided.innerRef}
											{...provided.draggableProps}
											{...provided.dragHandleProps}
										>
											<Card
												position={index + 1}
												roundRobinRoute={route}
												onDelete={() => {
													const results = [...routes];
													results.splice(index, 1);
													this.setState({routes: results}, () => this.saveRoutes())
												}}
											/>
										</Mui.Grid>
									)}
								</Draggable>
							))}
							{provided.placeholder}
						</Mui.Grid>
					)}
				</Droppable>
			</DragDropContext>
		);
	}

	// Confirmation when removing an agent entry from the rotation. If the agent appears more
	// than once, offer a checkbox to remove every entry (rather than just this slot).
	private renderRemoveModal() {
		const { routes, removeIndex, removeEntirely } = this.state;
		if (removeIndex === null) {
			return null;
		}
		const route = routes[removeIndex];
		if (!route) {
			return null;
		}
		const multiple = routes.filter((r) => r.agent.id === route.agent.id).length > 1;
		const close = () => this.setState({ removeIndex: null, removeEntirely: false });
		return (
			<ConfirmationModal
				open={true}
				title="Remove agent from rotation?"
				confirmLabel="Remove Agent"
				body={
					<>
						<Mui.Typography variant="body1">
							{multiple
								? "This agent appears in the rotation more than once. Removing this entry will adjust their lead distribution."
								: "This agent will no longer receive this lead assignment slot in the rotation."}
						</Mui.Typography>
						{multiple && (
							<Mui.FormControlLabel
								style={{ marginTop: 12 }}
								control={
									<Mui.Checkbox
										color="primary"
										checked={removeEntirely}
										onChange={(event) => this.setState({ removeEntirely: event.target.checked })}
									/>
								}
								label="Remove this agent from the rotation entirely"
							/>
						)}
					</>
				}
				onCancel={close}
				onConfirm={() => {
					const results = multiple && removeEntirely
						? routes.filter((r) => r.agent.id !== route.agent.id)
						: routes.filter((_, i) => i !== removeIndex);
					this.setState({ routes: results, removeIndex: null, removeEntirely: false }, () => this.saveRoutes());
				}}
			/>
		);
	}

	public render() {
		const { routes, dialogOpen } = this.state;
		const { onToggle } = this.props;
		const controlled = onToggle !== undefined;
		const enabled = controlled ? !!this.props.checked : this.state.enabled;
		const showOptions = !env.refreshDesignsDev || enabled;
		return (
			<>
				<Mui.Grid container spacing={2} direction="column">
					{!env.refreshDesignsDev && (
						<Mui.Grid item>
							<Mui.Typography>
								Assign leads to your agents in a rotating order. Drag and drop to change the order within the list.
								To create a weighted distribution, add an agent to the list more than once.
								Geographic and Featured Property routing take precedence over Round Robin
							</Mui.Typography>
						</Mui.Grid>
					)}
					<Mui.Grid item>
						<Switch
							checked={enabled}
							onChange={controlled ? onToggle! : (checked) => {
								this.setState({enabled: checked}, () => this.saveUser());
							}}
							label={env.refreshDesignsDev ? "Assign leads to agents in rotating order" : "Enabled"}
							description={env.refreshDesignsDev ? "When enabled, leads are assigned to agents in a rotating order based on the list below. To create a weighted distribution, add an agent to the list more than once." : undefined}
						/>
					</Mui.Grid>
					{showOptions && (env.refreshDesignsDev ? (
						<Mui.Grid item style={{ marginLeft: 59 }}>
							<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, maxWidth: 960 }}>
								<Mui.Typography style={{ fontWeight: "bold", fontSize: 16 }}>Agent Rotation Order</Mui.Typography>
								<Mui.Button
									variant="contained"
									color="primary"
									onClick={() => this.setState({ dialogOpen: !dialogOpen })}
									startIcon={<FeatherIcon fontSize="small"><Icons.Plus /></FeatherIcon>}
								>
									Add Agent
								</Mui.Button>
							</div>
							{routes.length === 0 ? (
								<Mui.Typography style={{ fontStyle: "italic", color: baseColors.gray }}>No agents added yet!</Mui.Typography>
							) : this.renderRefreshRotationList()}
						</Mui.Grid>
					) : (
						<>
						<Mui.Grid item>
							<Mui.Typography variant="h4">
								Agent Rotation Order
							</Mui.Typography>
						</Mui.Grid>
						<Mui.Grid item style={!enabled ? {pointerEvents: "none", opacity: .5}: {}}>
							<Mui.Button
								variant="contained"
								color={"primary"}
								onClick={() => this.setState({ dialogOpen: !dialogOpen})}
								startIcon={
									<FeatherIcon>
										<Icons.PlusCircle />
									</FeatherIcon>
								}
							>
								Add
							</Mui.Button>
						</Mui.Grid>
						<Mui.Grid item style={!enabled ? {opacity: .5}: {}}>
							{this.renderRotationList()}
						</Mui.Grid>
						</>
					))}
				</Mui.Grid>
				{dialogOpen && (() => {
					const dialogProps = {
						open: dialogOpen,
						onClose: () => this.setState({ dialogOpen: false }),
						onSave: (agents: Agent[]) => {
							const newRoutes: NewRoundRobinRoute[] = agents.map((agent) => {
								return {agent};
							});
							const results: NewRoundRobinRoute[] = [...routes] as NewRoundRobinRoute[];
							results.unshift(...newRoutes);
							this.props.updateRoundRobinRoutes({ roundRobinRoutes: results });
							this.setState({dialogOpen: false});
						},
					};
					return env.refreshDesignsDev
						? <RefreshAddDialog {...dialogProps} />
						: <AddDialog {...dialogProps} />;
				})()}
				{env.refreshDesignsDev && this.renderRemoveModal()}
			</>
		);
	}
}

export const RoundRobinTab = (withStyles(Router.withRouter(
	connect(mapStateToProps, mapDispatchToProps)(Component)
), styles));
